<template>
  <div ref="container" class="schedule-grid-container" :style="containerStyle">
    <button v-if="atRangeStart" type="button" class="schedule-grid__load-more" :style="loadEarlierStyle" @click="emit('loadEarlier')">
      ← Load earlier weeks
    </button>
    <button v-if="atRangeEnd" type="button" class="schedule-grid__load-more schedule-grid__load-more--end" @click="emit('loadLater')">
      Load later weeks →
    </button>
    <div ref="viewport" class="schedule-grid-viewport" @scroll.passive="syncEdges">
      <div ref="canvas" class="schedule-grid-canvas" :style="{ width: `${canvasWidth}px` }">
        <!-- The hovered PO's blocks already light up in every week they touch; this draws the
             route between them so the eye does not have to find them. -->
        <svg v-if="connectorRoutes.length > 0" class="schedule-grid__connectors" aria-hidden="true">
          <g v-for="route in connectorRoutes" :key="route.id" :stroke="route.color">
            <path :d="route.path" fill="none" />
            <circle v-for="(point, index) in route.points" :key="index" :cx="point.x" :cy="point.y" r="3.5" stroke="none" :fill="route.color" />
          </g>
        </svg>
        <table class="schedule-grid">
          <thead class="schedule-grid__header">
            <tr>
              <th
                v-for="header in leftHeaders"
                :key="header.id"
                class="schedule-grid__header-cell schedule-grid__sticky-column schedule-grid__corner"
                :class="{ 'schedule-grid__queue-header': header.column.id === 'queue' }"
                :style="columnStyle(header.column, header.getSize())"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
              <th v-if="paddingLeft > 0" class="schedule-grid__header-cell schedule-grid__spacer" :style="spacerStyle(paddingLeft)" aria-hidden="true"></th>
              <th
                v-for="header in visibleHeaders"
                :key="header.id"
                class="schedule-grid__header-cell"
                :data-current-period="header.column.id === currentWeekKey"
                :style="columnStyle(header.column, header.getSize())"
              >
                <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
              </th>
              <th v-if="paddingRight > 0" class="schedule-grid__header-cell schedule-grid__spacer" :style="spacerStyle(paddingRight)" aria-hidden="true"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tableRow in table.getRowModel().rows" :key="tableRow.id">
              <td
                v-for="cell in tableRow.getLeftVisibleCells()"
                :key="cell.id"
                class="schedule-grid__cell"
                :class="bodyCellClasses(cell)"
                :style="columnStyle(cell.column, cell.column.getSize())"
              >
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </td>
              <td
                v-if="paddingLeft > 0"
                class="schedule-grid__cell schedule-grid__spacer"
                :class="rowKindClasses(tableRow)"
                :style="spacerStyle(paddingLeft)"
                aria-hidden="true"
              ></td>
              <td
                v-for="cell in visibleCells(tableRow)"
                :key="cell.id"
                class="schedule-grid__cell"
                :class="bodyCellClasses(cell)"
                :style="columnStyle(cell.column, cell.column.getSize())"
              >
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </td>
              <td
                v-if="paddingRight > 0"
                class="schedule-grid__cell schedule-grid__spacer"
                :class="rowKindClasses(tableRow)"
                :style="spacerStyle(paddingRight)"
                aria-hidden="true"
              ></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    FlexRender,
    functionalUpdate,
    getCoreRowModel,
    useVueTable,
    type Cell,
    type Column,
    type ColumnDef,
    type ColumnPinningState,
    type Row,
  } from '@tanstack/vue-table';
  import { useVirtualizer } from '@tanstack/vue-virtual';
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import { useScheduleGridMetrics } from '@/components/scheduling/scheduleGridMetrics';
  import { useScheduleConnectors } from '@/composables/useScheduleConnectors';
  import type { BoardRow } from '@/utils/scheduleBoard';

  // Week columns rendered either side of the visible ones, so a fast scroll finds them
  // already mounted rather than blank.
  const WEEK_COLUMN_OVERSCAN = 3;
  // How close to either edge counts as "at the end of the loaded range".
  const EDGE_THRESHOLD = 24;
  // Clears the pinned vendor and queue columns the earlier-weeks button floats beside.
  const LOAD_EARLIER_GAP = 12;
  // Bottom padding of the page's <main>, left clear below the grid.
  const PAGE_BOTTOM_MARGIN = 24;
  const MIN_VIEWPORT_HEIGHT = 320;
  // Row heights declared in this file's CSS, needed as numbers to work out what is left over
  // for the Scheduled row.
  const HEADER_ROW_HEIGHT = 48;
  const TYPE_ROW_HEIGHT = 34;
  // The Scheduled cell's own vertical padding and bottom border, plus the viewport's two
  // borders — and a pixel of slack, since overshooting raises a vertical scrollbar that eats
  // horizontal space, while undershooting only leaves an invisible gap.
  const SCHEDULED_ROW_CHROME = 20;
  // The viewport's top and bottom borders, so a measured scrollbar can be told apart from them.
  const VIEWPORT_BORDER_HEIGHT = 2;
  const MIN_SCHEDULED_ROW_HEIGHT = 120;
  // Sub-pixel scroll positions mean "at the end" is never exactly zero room left.

  const props = defineProps<{
    rows: BoardRow[];
    columns: ColumnDef<BoardRow>[];
    currentWeekKey: string | null;
    // The block key of the PO singled out page-wide, so its connectors stay drawn after the
    // pointer leaves. Owned by the view (see useSelectedPo), never read from here.
    selectedBlockKey?: string | null;
  }>();
  const emit = defineEmits<{ loadEarlier: []; loadLater: [] }>();

  const { pinnedColumnsWidth, weekColumnWidth, setAvailableWidth } = useScheduleGridMetrics();

  const container = ref<HTMLElement | null>(null);
  const viewport = ref<HTMLElement | null>(null);
  const canvas = ref<HTMLElement | null>(null);
  const { routes: connectorRoutes } = useScheduleConnectors(
    canvas,
    computed(() => props.selectedBlockKey),
  );
  const viewportHeight = ref(0);
  // A classic (non-overlay) horizontal scrollbar sits inside the viewport's box and covers
  // whatever row is laid out under it. Measured, since overlay scrollbars take no room at all.
  const scrollbarHeight = ref(0);
  const atRangeStart = ref(false);
  const atRangeEnd = ref(false);
  const columnPinning = ref<ColumnPinningState>({ left: ['vendor', 'queue'] });

  const table = useVueTable({
    get data() {
      return props.rows;
    },
    get columns() {
      return props.columns;
    },
    getCoreRowModel: getCoreRowModel(),
    enableColumnPinning: true,
    state: {
      get columnPinning() {
        return columnPinning.value;
      },
    },
    onColumnPinningChange: (updater) => {
      columnPinning.value = functionalUpdate(updater, columnPinning.value);
    },
  });

  // The columns are flat — no header groups — so there is exactly one row of headers on
  // each side of the pin.
  const leftHeaders = computed(() => table.getLeftHeaderGroups()[0]?.headers ?? []);
  const centerHeaders = computed(() => table.getCenterHeaderGroups()[0]?.headers ?? []);

  // Only the week columns near the viewport are mounted: the full span runs to dozens of
  // them, each carrying a cell per capacity type, and rendering it whole is what made the
  // board slow. The pinned vendor and queue columns are outside this and always rendered.
  const columnVirtualizer = useVirtualizer(
    computed(() => ({
      count: centerHeaders.value.length,
      getScrollElement: () => viewport.value,
      estimateSize: () => weekColumnWidth.value,
      horizontal: true,
      overscan: WEEK_COLUMN_OVERSCAN,
      // The pinned vendor and queue columns sit ahead of the week columns in normal flow, so
      // the virtualized track starts that far into the scroll container.
      scrollMargin: pinnedColumnsWidth.value,
    })),
  );

  const virtualColumns = computed(() => columnVirtualizer.value.getVirtualItems());
  const visibleHeaders = computed(() => virtualColumns.value.map((virtualColumn) => centerHeaders.value[virtualColumn.index]));
  // Item offsets carry scrollMargin; the spacers measure the gap inside the virtualized
  // track alone, which getTotalSize() reports without it.
  const paddingLeft = computed(() => {
    const first = virtualColumns.value[0];
    return first ? first.start - pinnedColumnsWidth.value : 0;
  });
  const paddingRight = computed(() => {
    const last = virtualColumns.value.at(-1);
    return last ? columnVirtualizer.value.getTotalSize() - (last.end - pinnedColumnsWidth.value) : 0;
  });
  const canvasWidth = computed(() => pinnedColumnsWidth.value + columnVirtualizer.value.getTotalSize());
  const loadEarlierStyle = computed(() => ({ left: `${pinnedColumnsWidth.value + LOAD_EARLIER_GAP}px` }));

  // The Scheduled row takes whatever the capacity rows leave, so the whole grid lands inside
  // one screen. It has to be a hard number rather than "the rest": only the weeks near the
  // viewport are mounted, so a content-sized row would resize as the tallest week in the DOM
  // changed under a scroll. Weeks with more blocks than fit scroll inside their own cell.
  const typeRowCount = computed(() => props.rows.filter((row) => row.kind === 'type').length);
  const scheduledRowHeight = computed(() =>
    Math.max(
      MIN_SCHEDULED_ROW_HEIGHT,
      viewportHeight.value - HEADER_ROW_HEIGHT - typeRowCount.value * TYPE_ROW_HEIGHT - SCHEDULED_ROW_CHROME - scrollbarHeight.value,
    ),
  );

  // Read by the Scheduled cell's wrapper, which is built in a column definition and rendered
  // through FlexRender — it carries no scope id, so a scoped class could never reach it, but
  // a custom property inherits down the DOM regardless.
  const containerStyle = computed(() => ({
    '--schedule-viewport-height': viewportHeight.value > 0 ? `${viewportHeight.value}px` : undefined,
    '--schedule-row-height': `${scheduledRowHeight.value}px`,
  }));

  // How much room is left below the grid's own top edge. Measured rather than assumed: what
  // sits above it varies with the role (the customer legend is superadmin-only) and with
  // whether the vendor tabs wrap. scrollY makes it independent of the page's scroll position.
  function measureViewport(): void {
    if (!viewport.value) return;
    const top = viewport.value.getBoundingClientRect().top + window.scrollY;
    viewportHeight.value = Math.max(MIN_VIEWPORT_HEIGHT, window.innerHeight - top - PAGE_BOTTOM_MARGIN);
    scrollbarHeight.value = Math.max(0, viewport.value.offsetHeight - viewport.value.clientHeight - VIEWPORT_BORDER_HEIGHT);
    // clientWidth, so the column widths are chosen against the space inside the borders and
    // any scrollbar rather than the box around them.
    setAvailableWidth(viewport.value.clientWidth);
  }

  // The virtualizer memoises its measurements and does not treat estimateSize as an input, so
  // a changed column width needs measure() to make it recompute the offsets. The week at the
  // left edge is put back there afterwards, so a rotation does not also lose the reader's place.
  // Toggling the queue rail instead shifts the track's scrollMargin, which would appear to
  // scroll the weeks sideways; shifting scrollLeft by the same delta keeps them in place. One
  // watcher handles both because on a narrow layout a rail toggle changes both widths at once,
  // and the leading-week restore already lands on the right position in that case.
  watch([weekColumnWidth, pinnedColumnsWidth], ([width, pinned], [previousWidth, previousPinned]) => {
    const element = viewport.value;
    if (!element || !previousWidth) return;
    if (width !== previousWidth) {
      const leadingIndex = Math.round(element.scrollLeft / previousWidth);
      columnVirtualizer.value.measure();
      void nextTick(() => {
        element.scrollLeft = leadingIndex * width;
        syncEdges();
      });
      return;
    }
    element.scrollLeft += pinned - previousPinned;
    syncEdges();
  });

  function visibleCells(row: Row<BoardRow>): Cell<BoardRow, unknown>[] {
    const cells = row.getCenterVisibleCells();
    return virtualColumns.value.map((virtualColumn) => cells[virtualColumn.index]);
  }

  function spacerStyle(width: number): Record<string, string> {
    return { width: `${width}px`, minWidth: `${width}px` };
  }

  function bodyCellClasses(cell: Cell<BoardRow, unknown>): Record<string, boolean> {
    return {
      'schedule-grid__sticky-column': Boolean(cell.column.getIsPinned()),
      'schedule-grid__vendor-cell': cell.column.id === 'vendor',
      'schedule-grid__queue-cell': cell.column.id === 'queue',
      'schedule-grid__cell--current': cell.column.id === props.currentWeekKey,
      ...rowKindClasses(cell.row),
    };
  }

  // Capacity rows are read-only strips, so they get a fraction of the Scheduled
  // row's height — that row holds draggable PO cards and keeps it.
  function rowKindClasses(row: Row<BoardRow>): Record<string, boolean> {
    return { 'schedule-grid__cell--type': row.original.kind === 'type' };
  }

  function columnStyle(column: Column<BoardRow>, size: number): Record<string, string> {
    const style: Record<string, string> = { width: `${size}px`, minWidth: `${size}px` };
    if (column.getIsPinned() === 'left') {
      style.left = `${column.getStart('left')}px`;
    }
    return style;
  }

  function syncEdges(): void {
    const element = viewport.value;
    if (!element) return;
    atRangeStart.value = element.scrollLeft <= EDGE_THRESHOLD;
    atRangeEnd.value = element.scrollWidth - element.scrollLeft - element.clientWidth <= EDGE_THRESHOLD;
  }

  // Widening the range moves where the edges are without moving the scroll position, so no
  // scroll event follows — the buttons have to be re-checked when columns are added too.
  watch(
    () => centerHeaders.value.length,
    () => void nextTick(syncEdges),
  );

  // Can't find the week's header in the DOM — virtualization means it is only mounted when
  // it is near the viewport, which on the first call it is not.
  async function scrollToWeek(weekKey: string): Promise<void> {
    await nextTick();
    const index = centerHeaders.value.findIndex((header) => header.column.id === weekKey);
    if (!viewport.value || index < 0) {
      return;
    }
    // Deliberately not the virtualizer's scrollToIndex: before it has attached to the scroll
    // element it silently resolves every index to offset 0, which would open the board on the
    // oldest week. Every week column is exactly WEEK_COLUMN_WIDTH, so its offset is
    // arithmetic — and dropping the vendor column's width lands the week clear of the sticky
    // column rather than beneath it.
    viewport.value.scrollLeft = index * weekColumnWidth.value;
    syncEdges();
  }

  // Weeks prepended to the range shift every existing column right by their combined width;
  // without this the board would appear to jump that far into the future. Weeks appended to
  // the end need no such correction, which is why only this direction has a hook.
  function scrollByColumns(count: number): void {
    if (!viewport.value || count === 0) return;
    viewport.value.scrollLeft += count * weekColumnWidth.value;
    syncEdges();
  }

  let aboveObserver: ResizeObserver | null = null;

  onMounted(() => {
    measureViewport();
    window.addEventListener('resize', measureViewport);
    // Anything above the grid changing height moves its top — the vendor tabs wrapping, the
    // customer legend appearing once mappings load. The grid's own height never feeds back
    // into this: it is the last element in the column, so its top does not depend on it.
    const above = container.value?.parentElement;
    if (above) {
      aboveObserver = new ResizeObserver(measureViewport);
      aboveObserver.observe(above);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', measureViewport);
    aboveObserver?.disconnect();
  });

  defineExpose({ scrollToWeek, scrollByColumns });
</script>

<style scoped>
  .schedule-grid-container {
    position: relative;
  }

  /* Floats over the grid rather than living in it: a real column would take a week's worth
     of width permanently, and these are only reachable at the very ends of the range. */
  .schedule-grid__load-more {
    position: absolute;
    z-index: 25;
    top: 50%;
    padding: 0.375rem 0.75rem;
    border: 1px solid rgb(203 213 225);
    border-radius: 0.5rem;
    background: white;
    box-shadow: 0 2px 6px rgb(15 23 42 / 12%);
    color: rgb(51 65 85);
    font-size: 0.75rem;
    font-weight: 600;
    transform: translateY(-50%);
  }

  /* No pinned column on this side, so it sits against the edge. */
  .schedule-grid__load-more--end {
    right: 0.75rem;
  }

  .schedule-grid__load-more:hover {
    border-color: rgb(96 165 250);
    color: rgb(29 78 216);
  }

  .schedule-grid-viewport {
    width: 100%;
    /* Set from the measured distance to the bottom of the window; the fallback only ever
       shows on the frame before that measurement lands. */
    max-height: var(--schedule-viewport-height, calc(100vh - 12rem));
    overflow: auto;
    /* The board is a scroller inside a scrollable page. Without this, a wheel gesture that
       runs a week's card list (or the grid) to its end hands the leftover delta to the
       document, and the whole page shifts under a scroll meant for the table. */
    overscroll-behavior: contain;
    border: 1px solid rgb(209 213 219);
    border-radius: 0.75rem;
    background: white;
    box-shadow: 0 1px 3px rgb(15 23 42 / 8%);
  }

  .schedule-grid {
    position: relative;
    /* table-layout: fixed only engages with a non-auto width; without it the
       current week's crowded cells widen their column past the declared size */
    width: 100%;
    min-width: 100%;
    table-layout: fixed;
    border-spacing: 0;
    border-collapse: separate;
  }

  .schedule-grid-canvas {
    position: relative;
    min-width: 100%;
  }

  /* Above the cards (z-index 6 on .schedule-card) so the route runs over the blocks it crosses
     rather than vanishing behind them (Sep 8 review), below the sticky vendor column (10) and
     header (20) so it never draws over either. Never a pointer target — the line exists
     because something is hovered, and must not break that hover. */
  .schedule-grid__connectors {
    position: absolute;
    inset: 0;
    z-index: 7;
    overflow: visible;
    pointer-events: none;
  }

  /* Stroke lives here rather than on the element so the dash stays in proportion to the
     width: scaled up without lengthening the gaps, the line closes into a near-solid rule. */
  .schedule-grid__connectors path {
    stroke-width: 2.5;
    stroke-dasharray: 5 4;
    stroke-linecap: round;
    animation: schedule-connector-flow 700ms linear infinite;
  }

  /* The dashes travel the way the path was drawn — earliest week to latest — so the line
     says which end is upstream rather than only which blocks are related. One dash-plus-gap
     per iteration (5 + 4), otherwise the pattern jumps as the loop restarts. */
  @keyframes schedule-connector-flow {
    to {
      stroke-dashoffset: -9;
    }
  }

  /* Fades in on the transition from nothing drawn to something drawn. Positions update
     inside the same <svg>, so panning with a PO selected never replays it. */
  .schedule-grid__connectors {
    animation: schedule-connector-appear 160ms ease-out;
  }

  @keyframes schedule-connector-appear {
    from {
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .schedule-grid__connectors,
    .schedule-grid__connectors path {
      animation: none;
    }
  }

  .schedule-grid__header {
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .schedule-grid__header-cell,
  .schedule-grid__cell {
    border-right: 1px solid rgb(226 232 240);
    border-bottom: 1px solid rgb(226 232 240);
    text-align: left;
    vertical-align: middle;
    white-space: nowrap;
  }

  .schedule-grid__header-cell:not(.schedule-grid__queue-header) {
    height: 48px;
    padding: 0.75rem;
    background: rgb(248 250 252);
    color: rgb(71 85 105);
    font-size: 0.75rem;
    font-weight: 600;
  }

  /* The queue column keeps its amber tint in the header too — the body cell already carries
     it (.schedule-grid__queue-cell); only the header was falling through to the slate-50 rule
     above because it never received a per-column class of its own. */
  .schedule-grid__queue-header {
    height: 48px;
    background: #fffbeb;
    color: rgb(71 85 105);
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0 0.75rem;
  }

  .schedule-grid__header-cell[data-current-period='true'] {
    background: rgb(239 246 255);
    color: rgb(29 78 216);
  }

  .schedule-grid__cell--current:not(.schedule-grid__vendor-cell) {
    background: rgb(239 246 255 / 45%);
  }

  .schedule-grid__cell {
    height: 80px;
    padding: 0.5rem 0.75rem;
    background: white;
    color: rgb(51 65 85);
    font-size: 0.875rem;
    white-space: normal;
  }

  .schedule-grid__cell--type {
    height: 34px;
    padding: 0.25rem 0.5rem;
  }

  /* Stands in for the week columns scrolled out of the DOM. It is off-screen by
     definition, so it carries no divider of its own. */
  .schedule-grid__spacer {
    padding: 0;
    border-right: 0;
  }

  /* Skips the current week: striping is far more specific than the highlight, so without
     this it erases it on every other row. */
  .schedule-grid tbody tr:nth-child(even) .schedule-grid__cell:not(.schedule-grid__vendor-cell):not(.schedule-grid__queue-cell):not(.schedule-grid__cell--current) {
    background: rgb(248 250 252 / 65%);
  }

  .schedule-grid tbody tr:nth-child(even) .schedule-grid__cell.schedule-grid__queue-cell {
    background: #fffdf7;
  }

  .schedule-grid__queue-cell {
    background: #fffdf7;
    padding: 0;
  }

  .schedule-grid tbody tr:first-child .schedule-grid__cell {
    border-top: 0;
  }

  .schedule-grid tbody tr:last-child .schedule-grid__cell {
    border-bottom: 0;
  }

  .schedule-grid__sticky-column {
    position: sticky;
    z-index: 10;
    box-shadow: 1px 0 0 rgb(203 213 225);
  }

  .schedule-grid__corner {
    z-index: 30;
  }

  .schedule-grid__header-cell:last-child,
  .schedule-grid__cell:last-child {
    border-right: 0;
  }

</style>
